<template>
  <div ref="containerRef" class="SeatMap" role="grid">
    <SeatMapWings
      v-if="wingsSlot"
      :range="wingsSlot.range"
      :rows="rowElements"
      :container="containerRef"
    />

    <div
      v-for="section of schema.layout.sections"
      :key="section.id"
      class="SeatMap-section"
      role="rowgroup"
    >
      <div
        v-if="section.description"
        class="SeatMap-sectionDescription"
        :class="section.description.seatClass && `SeatMap-sectionDescription--${section.description.seatClass}`"
      >
        <h3 class="SeatMap-sectionHeading">
          {{ section.description.heading }}
        </h3>
        <p class="SeatMap-sectionContent">
          {{ section.description.content }}
        </p>
      </div>

      <div
        v-for="(row, rowIndex) of section.rows"
        :key="row.rowNumber ?? 'noseat-' + rowIndex"
        :ref="row.isSeatsRow && row.rowNumber !== undefined ? setRowRef(row.rowNumber) : undefined"
        class="SeatMap-row"
        :class="{ 'SeatMap-row--spread': !row.isSeatsRow }"
        role="row"
      >
        <div
          v-for="(unit, unitIndex) of row.items"
          :key="unit.key || unitIndex"
          class="SeatMap-cell"
          :class="{ 'SeatMap-cell--aisle': unit.type === 'aisle' }"
          role="gridcell"
          :data-seat-key="unit.type === 'seat' ? unit.key : undefined"
        >
          <slot :unit="unit" :status="statusFor(unit)">
            <SeatCell
              v-if="unit.type === 'seat'"
              :label="labelFor(unit)"
              :state="statusFor(unit)"
              :variant="unit.seatClass"
              :ariaLabel="ariaLabelFor(unit)"
              @click="onSeatClick(unit)"
            />
            <SeatMapUnit
              v-else
              :type="unit.type"
              :direction="unit.direction"
              :text="unit.key"
            />
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, type ComponentPublicInstance } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { getInitials } from '@cvo/domain/utils';
import SeatCell from '../../atoms/SeatCell/SeatCell.vue';
import type { SeatCellState } from '../../atoms/SeatCell/SeatCell.model';
import SeatMapUnit from './SeatMapUnit.vue';
import SeatMapWings from './SeatMapWings.vue';
import type { SeatMapEmits, SeatMapProps, SeatMapUnit as SeatMapUnitModel } from './SeatMap.model';

const props = defineProps<SeatMapProps>();

const emit = defineEmits<SeatMapEmits>();

const { t } = useI18n();

const containerRef = useTemplateRef<HTMLDivElement>('containerRef');

const rowElements = ref<Partial<Record<number, HTMLElement>>>({});

function setRowRef(rowNumber: number) {
  return (element: Element | ComponentPublicInstance | null) => {
    if (element instanceof HTMLElement) rowElements.value[rowNumber] = element;
  };
}

const wingsSlot = computed(() => props.schema.layout.slots?.wings);

function statusFor(unit: SeatMapUnitModel): SeatCellState {
  if (props.assignments?.[unit.key]) return 'selected';
  return props.availability?.[unit.key] ?? 'available';
}

function labelFor(unit: SeatMapUnitModel): string {
  const passenger = props.assignments?.[unit.key];
  return passenger ? getInitials(passenger.name) : unit.key;
}

function onSeatClick(unit: SeatMapUnitModel): void {
  const status = statusFor(unit);
  if (status === 'unavailable' || status === 'disabled') return;

  emit('seatClick', unit);
}

const ARIA_LABEL_KEYS: Record<SeatCellState, string> = {
  available: 'ui.components.seatMap.seatCell.ariaLabelAvailable',
  selected: 'ui.components.seatMap.seatCell.ariaLabelSelected',
  disabled: 'ui.components.seatMap.seatCell.ariaLabelDisabled',
  unavailable: 'ui.components.seatMap.seatCell.ariaLabelUnavailable',
};

function ariaLabelFor(unit: SeatMapUnitModel): string {
  return t(ARIA_LABEL_KEYS[statusFor(unit)], { seat: unit.key });
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .SeatMap {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-5;
    width: fit-content;
    border-width: vars.$organisms-seatmap-border-stroke;
    border-style: solid;
    border-color: vars.$organisms-seatmap-border-color;
    // The fuselage nose/tail is a full dome in Figma; only the top corners use the regular radius.
    border-start-start-radius: vars.$organisms-seatmap-border-radius;
    border-start-end-radius: vars.$organisms-seatmap-border-radius;
    border-end-start-radius: vars.$organisms-seatmap-train-border-radius;
    border-end-end-radius: vars.$organisms-seatmap-train-border-radius;
    background-color: vars.$organisms-seatmap-bg-color;
    padding-block: vars.$organisms-seatmap-lg-md-padding-top-bottom;
    padding-inline: vars.$organisms-seatmap-lg-md-padding-left-right;
    // Extra room so the last row isn't visually clipped by the domed bottom border-radius.
    padding-block-end: calc(#{vars.$organisms-seatmap-lg-md-padding-top-bottom} + #{vars.$dimensions-d-30});

    @include responsive.mobile {
      padding-block: vars.$organisms-seatmap-sm-padding-top-bottom;
      padding-inline: vars.$organisms-seatmap-sm-padding-left-right;
      padding-block-end: calc(#{vars.$organisms-seatmap-sm-padding-top-bottom} + #{vars.$dimensions-d-30});
    }

    &-section {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
    }

    &-sectionHeading {
      color: vars.$atoms-seatrow-seata-text-color;
      @include text.text('md', vars.$typography-weight-strong);
    }

    &-sectionContent {
      color: vars.$atoms-seatrow-seata-secondary-text-color;
      @include text.text('sm');
    }

    &-sectionDescription--basic {
      .SeatMap-sectionHeading {
        color: vars.$atoms-seatrow-seatb-text-color;
      }

      .SeatMap-sectionContent {
        color: vars.$atoms-seatrow-seatb-secondary-text-color;
      }
    }

    &-row {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;

      // Rows with no aisle to grow into (WC/galley header, exit banners, column labels) spread
      // their few items edge-to-edge instead of packing them to the left.
      &--spread {
        justify-content: space-between;
      }
    }

    &-cell {
      display: flex;
      align-items: center;
      justify-content: center;

      &--aisle {
        flex: 1 0 auto;
      }
    }
  }
}
</style>
